import type { MetricItem } from "@/components/key-metrics"
import { daysUntil, type TermTimelinePosition } from "@/lib/evaluation-term-utils"
import {
  EVALUATION_REFERENCE_DATE,
  MOCK_EVALUATION_TERMS,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import {
  buildTermWorkQueueRows,
  type EvaluationWorkQueueRow,
} from "@/lib/mock/evaluation-work-queue"

const TIMELINE_SORT: Record<TermTimelinePosition, number> = {
  current: 0,
  last: 1,
  upcoming: 2,
  future: 3,
  past: 4,
}

/** Terms with active survey operations — excludes archived past and empty future terms. */
export function getOperationsDashboardTerms(): EvaluationTerm[] {
  return MOCK_EVALUATION_TERMS.filter(term => {
    if (term.timelinePosition === "past") return false
    if (term.timelinePosition === "future" && term.courseOfferings === 0) return false
    return true
  }).sort(
    (a, b) =>
      TIMELINE_SORT[a.timelinePosition] - TIMELINE_SORT[b.timelinePosition] ||
      a.startDate.localeCompare(b.startDate),
  )
}

export function buildOperationsTermSections(): {
  term: EvaluationTerm
  rows: EvaluationWorkQueueRow[]
}[] {
  const refDate = EVALUATION_REFERENCE_DATE
  return getOperationsDashboardTerms()
    .map(term => ({
      term,
      rows: buildTermWorkQueueRows(term, refDate),
    }))
    .filter(section => section.rows.length > 0)
}

export const OPERATIONS_KPI_PRIOR = {
  avgResponseRate: 58,
  closingThisWeek: 18,
  needsSetup: 48,
  readyToPublish: 42,
} as const

/** >= this count escalates a backlog KPI from "warning" to "danger". */
const BACKLOG_DANGER_THRESHOLD = 10
/** Below this average response rate, live collection is considered at risk. */
const LOW_RESPONSE_RATE_THRESHOLD = 50

function countDelta(
  current: number,
  prior: number,
): { delta: string; trend: "up" | "down" | "neutral" } {
  const diff = current - prior
  if (diff === 0) return { delta: "", trend: "neutral" }
  return {
    delta: diff > 0 ? `+${diff}` : String(diff),
    trend: diff > 0 ? "up" : "down",
  }
}

function backlogAlert(count: number): "warning" | "danger" | undefined {
  if (count >= BACKLOG_DANGER_THRESHOLD) return "danger"
  if (count > 0) return "warning"
  return undefined
}

/** Cross-term operations KPI strip (max four tiles). */
export function evaluationOperationsKpiMetrics(terms: EvaluationTerm[]): MetricItem[] {
  const refDate = EVALUATION_REFERENCE_DATE

  let closingThisWeek = 0
  let needsSetup = 0
  let readyToPublish = 0
  let responseWeightedSum = 0
  let responseWeightTotal = 0
  let liveTermCount = 0

  for (const term of terms) {
    const live = term.counts.live
    needsSetup += term.counts.notSetup + term.counts.draft

    if (live > 0) {
      liveTermCount += 1
      if (term.liveResponseRate !== undefined) {
        responseWeightedSum += term.liveResponseRate * live
        responseWeightTotal += live
      }
    }

    if (term.nextClosingDate && live > 0) {
      const days = daysUntil(term.nextClosingDate, refDate)
      if (days >= 0 && days <= 7) {
        closingThisWeek += live
      }
    }

    if (
      term.counts.closed > 0 &&
      term.counts.published === 0 &&
      (term.timelinePosition === "last" || term.timelinePosition === "current")
    ) {
      readyToPublish += term.counts.closed
    }
  }

  const avgResponseRate =
    responseWeightTotal > 0 ? Math.round(responseWeightedSum / responseWeightTotal) : undefined
  const responseRateLow = avgResponseRate !== undefined && avgResponseRate < LOW_RESPONSE_RATE_THRESHOLD

  return [
    {
      id: "response-rate",
      label: "Avg response rate (live)",
      value: avgResponseRate !== undefined ? `${avgResponseRate}%` : "None",
      ...(avgResponseRate !== undefined
        ? countDelta(avgResponseRate, OPERATIONS_KPI_PRIOR.avgResponseRate)
        : { delta: "", trend: "neutral" as const }),
      trendPolarity: "higher_is_better",
      alert: responseRateLow ? "warning" : undefined,
      description:
        liveTermCount > 0
          ? `Across ${liveTermCount} term${liveTermCount === 1 ? "" : "s"} with live windows · vs last week`
          : "vs last week",
      href: "#eval-operations-terms",
    },
    {
      id: "closing",
      label: "Closing this week",
      value: closingThisWeek,
      ...countDelta(closingThisWeek, OPERATIONS_KPI_PRIOR.closingThisWeek),
      trendPolarity: "informational",
      alert: closingThisWeek > 0 && responseRateLow ? "warning" : undefined,
      description:
        closingThisWeek > 0
          ? responseRateLow
            ? "Live windows ending within 7 days · response rate is low"
            : "Live windows ending within 7 days · vs last week"
          : "vs last week",
      href: "#eval-operations-terms",
    },
    {
      id: "needs-setup",
      label: "Needs setup",
      value: needsSetup,
      ...countDelta(needsSetup, OPERATIONS_KPI_PRIOR.needsSetup),
      trendPolarity: "lower_is_better",
      alert: backlogAlert(needsSetup),
      description: needsSetup > 0 ? "Courses without a scheduled window · vs last week" : "vs last week",
      href: "#eval-operations-terms",
    },
    {
      id: "ready-publish",
      label: "Ready to publish",
      value: readyToPublish,
      ...countDelta(readyToPublish, OPERATIONS_KPI_PRIOR.readyToPublish),
      trendPolarity: "lower_is_better",
      alert: backlogAlert(readyToPublish),
      description: readyToPublish > 0 ? "Closed evaluations awaiting publish · vs last week" : "vs last week",
      href: "#eval-operations-terms",
    },
  ]
}
